import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Quote } from 'lucide-react';
import { testimonialsData } from '../mock';

export const Testimonials = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-32 bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Number */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="flex items-center gap-4">
            <span className="text-8xl md:text-9xl font-bold text-teal-100">04</span>
            <div>
              <span className="block text-teal-600 font-semibold text-sm uppercase tracking-wider">Chapter Four</span>
              <span className="block text-2xl font-semibold text-gray-900">Client Reviews</span>
            </div>
          </div>
        </motion.div>

        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Loved by Homes & Businesses Alike
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Don't just take our word for it — here's what our clients have to say.
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonialsData.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 50, rotateY: -15 }}
              animate={isInView ? { opacity: 1, y: 0, rotateY: 0 } : { opacity: 0, y: 50, rotateY: -15 }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              whileHover={{ y: -10, rotateY: 5 }}
              className="relative"
              style={{ perspective: '1000px' }}
            >
              <div className="bg-white rounded-3xl p-8 shadow-lg shadow-teal-100/50 border border-gray-100 h-full flex flex-col relative overflow-hidden">
                {/* Quote Icon */}
                <div className="absolute top-6 right-6 opacity-10">
                  <Quote className="w-20 h-20 text-teal-600" />
                </div>

                {/* Avatar */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={isInView ? { scale: 1 } : { scale: 0 }}
                  transition={{ delay: index * 0.15 + 0.3, type: 'spring', stiffness: 200 }}
                  className="relative mb-6"
                >
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-2xl font-bold text-white shadow-lg">
                    {testimonial.avatar}
                  </div>
                </motion.div>

                {/* Text */}
                <p className="text-gray-700 text-lg leading-relaxed mb-6 flex-1 relative z-10">
                  "{testimonial.text}"
                </p>

                {/* Author */}
                <div className="border-t border-gray-100 pt-4 relative z-10">
                  <div className="font-bold text-gray-900">{testimonial.name}</div>
                  <div className="text-teal-600 text-sm font-medium">{testimonial.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
